import React, { useMemo, useState } from "react";

// Sits under the figure it qualifies — "unrated" under revenue, "uncosted"
// under direct cost — so the caveat is attached to the number it makes
// unreliable rather than to the row as a whole. Rendered at row, subtotal
// AND total level: an aggregate that hides the gap its rows admit to is
// the more misleading of the two.
function GapNote({ hours, kind }) {
  if (!(Number(hours) > 0)) return null;
  const n = Number(hours).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const selling = kind === "rates";
  return (
    <span
      title={
        selling
          ? `${n} h have no selling rate in this period and contribute €0 revenue`
          : `${n} h have no cost rate in this period and contribute €0 direct cost — the margin is overstated by whatever they really cost`
      }
      style={{
        display: "block",
        fontSize: 10,
        fontWeight: 500,
        color: "#b45309",
        marginTop: 1,
        whiteSpace: "nowrap",
      }}
    >
      {n} h {selling ? "unrated" : "uncosted"}
    </span>
  );
}

export default function ConsultantClientTable({ data = [] }) {
  const [sortKey, setSortKey] = useState(null);
  const [direction, setDirection] = useState("desc");
  const [hoveredRow, setHoveredRow] = useState(null);

  function toggleSort(key) {
    if (sortKey === key) {
      setDirection((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortKey(key);
      setDirection("desc");
    }
  }

  const fmt = (n) =>
    Number(n || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  function getMarginColor(m) {
    // Absolute-euro banding. Ordered high -> low so every branch is reachable:
    // green for strong margins, orange for the middle band, red for weak/negative.
    // Mirrors ClientTreemapTable.jsx (finding #50).
    if (m >= 1500) return "#1D9E75";
    if (m >= 500) return "#f97316";
    return "#e03e3e";
  }

  const arrow = (key) =>
    sortKey === key ? (direction === "desc" ? " ↓" : " ↑") : "";

  // Accessibility props for sortable column headers (finding #49): operable by
  // keyboard (Enter/Space) and announced as a sort button with current state.
  const sortableHeaderProps = (key, label) => ({
    role: "button",
    tabIndex: 0,
    "aria-label": `Sort by ${label}${
      sortKey === key ? `, currently ${direction}ending` : ""
    }`,
    onClick: () => toggleSort(key),
    onKeyDown: (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        toggleSort(key);
      }
    },
  });

  const groupedByConsultant = useMemo(() => {
    let rows = [...data];
    if (sortKey) {
      rows.sort((a, b) => {
        const av = a[sortKey] || 0;
        const bv = b[sortKey] || 0;
        return direction === "asc" ? av - bv : bv - av;
      });
    }

    const consultantOrder = [];
    const consultantMap = {};

    rows.forEach((r) => {
      const user = r.user;
      if (!consultantMap[user]) {
        consultantMap[user] = [];
        consultantOrder.push(user);
      }
      consultantMap[user].push(r);
    });

    return consultantOrder.map((user) => {
      const userRows = consultantMap[user];
      const subtotal = userRows.reduce(
        (acc, r) => {
          acc.hours              += Number(r.hours              || 0);
          acc.revenue            += Number(r.revenue            || 0);
          acc.overhead           += Number(r.overhead           || 0);
          acc.directCost         += Number(r.directCost         || 0);
          acc.margin             += Number(r.margin             || 0);
          acc.hoursMissingRates  += Number(r.hoursMissingRates  || 0);
          acc.hoursMissingCosts  += Number(r.hoursMissingCosts  || 0);
          return acc;
        },
        { hours: 0, revenue: 0, overhead: 0, directCost: 0, margin: 0,
          hoursMissingRates: 0, hoursMissingCosts: 0 }
      );
      return { user, rows: userRows, subtotal };
    });
  }, [data, sortKey, direction]);

  const totals = useMemo(() => {
    return data.reduce(
      (acc, r) => {
        acc.hours      += Number(r.hours      || 0);
        acc.revenue    += Number(r.revenue    || 0);
        acc.overhead   += Number(r.overhead   || 0);
        acc.directCost += Number(r.directCost || 0);
        acc.margin            += Number(r.margin            || 0);
        acc.hoursMissingRates += Number(r.hoursMissingRates || 0);
        acc.hoursMissingCosts += Number(r.hoursMissingCosts || 0);
        return acc;
      },
      { hours: 0, revenue: 0, overhead: 0, directCost: 0, margin: 0,
        hoursMissingRates: 0, hoursMissingCosts: 0 }
    );
  }, [data]);

  if (!data.length) return null;

  const thStyle = {
    textAlign: "right",
    padding: "10px 12px",
    fontSize: 11,
    fontWeight: 600,
    color: "#6b7280",
    borderBottom: "1px solid #e5e7eb",
    cursor: "pointer",
    whiteSpace: "nowrap",
    background: "#ffffff",
  };

  const tdBase = {
    textAlign: "right",
    padding: "9px 12px",
    fontSize: 13,
    color: "#111827",
    borderBottom: "1px solid #f3f4f6",
    transition: "background 0.15s ease",
  };

  const getRowStyle = (rowKey, baseBackground = "transparent") => ({
    background: hoveredRow === rowKey ? "#f0fdf8" : baseBackground,
    boxShadow: hoveredRow === rowKey ? "0 2px 8px rgba(0,0,0,0.06)" : "none",
    transition: "background 0.15s ease, box-shadow 0.15s ease",
    cursor: "default",
  });

  return (
    <div style={{
      background: "#ffffff",
      border: "1px solid #e5e7eb",
      borderRadius: 10,
      padding: 16,
      marginTop: 12,
      overflow: "auto",
    }}>

      <div style={{ marginBottom: 14 }}>
        <span style={{
          fontSize: 11,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: "#6b7280",
        }}>
          Consultants × Clients
        </span>
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr>
            <th style={{ ...thStyle, textAlign: "left", width: "16%" }}>Consultant</th>
            <th style={{ ...thStyle, textAlign: "left", width: "14%" }}>Client</th>
            <th style={thStyle} {...sortableHeaderProps("hours", "Hours")}>Hours{arrow("hours")}</th>
            <th style={thStyle} {...sortableHeaderProps("revenue", "Revenue")}>Revenue (€){arrow("revenue")}</th>
            <th style={thStyle} {...sortableHeaderProps("overhead", "Overhead")}>Overhead (€){arrow("overhead")}</th>
            <th style={thStyle} {...sortableHeaderProps("directCost", "Direct cost")}>Direct cost (€){arrow("directCost")}</th>
            <th style={thStyle} {...sortableHeaderProps("margin", "Margin")}>Margin (€){arrow("margin")}</th>
          </tr>
        </thead>

        <tbody>
          {groupedByConsultant.map(({ user, rows, subtotal }) => (
            <React.Fragment key={user}>

              {rows.map((r, idx) => {
                const rowKey = `${r.user}||${r.project}||${idx}`;
                return (
                  <tr
                    key={rowKey}
                    onMouseEnter={() => setHoveredRow(rowKey)}
                    onMouseLeave={() => setHoveredRow(null)}
                    style={getRowStyle(rowKey)}
                  >
                    <td style={{ ...tdBase, textAlign: "left", fontWeight: idx === 0 ? 500 : 400, color: "#374151" }}>
                      {idx === 0 ? r.user : ""}
                    </td>
                    <td style={{ ...tdBase, textAlign: "left", color: "#6b7280" }}>{r.project}</td>
                    <td style={tdBase}>{fmt(r.hours)}</td>
                    <td style={tdBase}>
                      {fmt(r.revenue)}
                      <GapNote hours={r.hoursMissingRates} kind="rates" />
                    </td>
                    <td style={tdBase}>{fmt(r.overhead)}</td>
                    <td style={tdBase}>
                      {fmt(r.directCost)}
                      <GapNote hours={r.hoursMissingCosts} kind="costs" />
                    </td>
                    <td style={{ ...tdBase, fontWeight: 600, color: getMarginColor(r.margin) }}>
                      {fmt(r.margin)}
                    </td>
                  </tr>
                );
              })}

              {/* Subtotal */}
              {(() => {
                const rowKey = `subtotal-${user}`;
                return (
                  <tr
                    onMouseEnter={() => setHoveredRow(rowKey)}
                    onMouseLeave={() => setHoveredRow(null)}
                    style={getRowStyle(rowKey, "#f9fafb")}
                  >
                    <td style={{ ...tdBase, textAlign: "left", borderBottom: "1px solid #e5e7eb", background: "transparent" }}></td>
                    <td style={{ ...tdBase, textAlign: "left", fontWeight: 600, color: "#374151", borderBottom: "1px solid #e5e7eb", background: "transparent" }}>
                      Subtotal {user}
                    </td>
                    <td style={{ ...tdBase, fontWeight: 600, borderBottom: "1px solid #e5e7eb", background: "transparent" }}>{fmt(subtotal.hours)}</td>
                    <td style={{ ...tdBase, fontWeight: 600, borderBottom: "1px solid #e5e7eb", background: "transparent" }}>
                      {fmt(subtotal.revenue)}
                      <GapNote hours={subtotal.hoursMissingRates} kind="rates" />
                    </td>
                    <td style={{ ...tdBase, fontWeight: 600, borderBottom: "1px solid #e5e7eb", background: "transparent" }}>{fmt(subtotal.overhead)}</td>
                    <td style={{ ...tdBase, fontWeight: 600, borderBottom: "1px solid #e5e7eb", background: "transparent" }}>
                      {fmt(subtotal.directCost)}
                      <GapNote hours={subtotal.hoursMissingCosts} kind="costs" />
                    </td>
                    <td style={{ ...tdBase, fontWeight: 700, color: getMarginColor(subtotal.margin), borderBottom: "1px solid #e5e7eb", background: "transparent" }}>
                      {fmt(subtotal.margin)}
                    </td>
                  </tr>
                );
              })()}

            </React.Fragment>
          ))}

          {/* TOTAL global */}
          {(() => {
            const rowKey = "total-global";
            return (
              <tr
                onMouseEnter={() => setHoveredRow(rowKey)}
                onMouseLeave={() => setHoveredRow(null)}
                style={getRowStyle(rowKey, "#f3f4f6")}
              >
                <td style={{ ...tdBase, textAlign: "left", fontWeight: 700, borderBottom: "none", borderTop: "2px solid #e5e7eb", background: "transparent" }}>
                  TOTAL
                </td>
                <td style={{ ...tdBase, borderBottom: "none", borderTop: "2px solid #e5e7eb", background: "transparent" }}></td>
                <td style={{ ...tdBase, fontWeight: 700, borderBottom: "none", borderTop: "2px solid #e5e7eb", background: "transparent" }}>{fmt(totals.hours)}</td>
                <td style={{ ...tdBase, fontWeight: 700, borderBottom: "none", borderTop: "2px solid #e5e7eb", background: "transparent" }}>
                  {fmt(totals.revenue)}
                  <GapNote hours={totals.hoursMissingRates} kind="rates" />
                </td>
                <td style={{ ...tdBase, fontWeight: 700, borderBottom: "none", borderTop: "2px solid #e5e7eb", background: "transparent" }}>{fmt(totals.overhead)}</td>
                <td style={{ ...tdBase, fontWeight: 700, borderBottom: "none", borderTop: "2px solid #e5e7eb", background: "transparent" }}>
                  {fmt(totals.directCost)}
                  <GapNote hours={totals.hoursMissingCosts} kind="costs" />
                </td>
                <td style={{ ...tdBase, fontWeight: 700, color: getMarginColor(totals.margin), borderBottom: "none", borderTop: "2px solid #e5e7eb", background: "transparent" }}>
                  {fmt(totals.margin)}
                </td>
              </tr>
            );
          })()}
        </tbody>
      </table>
    </div>
  );
}